import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import { ShieldCheck, Webhook, Euro, Workflow, CircleCheck, Check } from 'lucide-react';
import styles from './ValueProposition.module.css';

const CARD_ICONS = [ShieldCheck, Webhook, Euro, Workflow];

const CARD_SLOTS = [
  { itemIdx: 0, angle: 225 },
  { itemIdx: 2, angle: 315 },
  { itemIdx: 1, angle: 135 },
  { itemIdx: 3, angle: 45 },
];

const ELLIPSES = [
  { rxMult: 1.1, ryMult: 0.55, rotation: -30, dur: 5, delay: 0 },
  { rxMult: 1.2, ryMult: 0.5, rotation: 35, dur: 7, delay: -2 },
  { rxMult: 0.9, ryMult: 0.6, rotation: 78, dur: 9, delay: -5 },
];

const PRODUCTS_COUNT = 4;
const SLIDE_COUNT = PRODUCTS_COUNT + 2;
const SLIDE_PRODUCT_MAP = [3, 0, 1, 2, 3, 0];
const CARD_W = 540;
const CARD_GAP = 22;
const STEP = CARD_W + CARD_GAP;
const BASE_OFFSET = CARD_W / 2 + STEP;
const ACTIVE_DURATION = 5200;
const SLIDE_DURATION = 800;

const FALLBACK_IMAGE = '/images/iphone-14-pro-space-black.webp';

interface Product {
  name: string;
  image: string;
  specs: string[];
  checks: string[];
}

export function ValueProposition() {
  const { t } = useTranslation('home');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [activeCard, setActiveCard] = useState(-1);
  const [doneCards, setDoneCards] = useState<number[]>([0]);
  const hasAnimated = useRef(false);

  const items = t('valueProposition.items', { returnObjects: true }) as { title: string; text: string }[];
  const products = t('valueProposition.products', { returnObjects: true }) as Product[];

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    function schedule(fn: () => void, ms: number) {
      timers.push(setTimeout(() => { if (!cancelled) fn(); }, ms));
    }

    function runCycle(delay: number) {
      if (cancelled) return;
      let t = delay;

      for (let step = 0; step < PRODUCTS_COUNT; step++) {
        const s = step;
        schedule(() => setActiveCard(s), t);
        t += ACTIVE_DURATION;

        const nextDone = Array.from({ length: s + 2 }, (_, i) => i);
        schedule(() => {
          setActiveCard(-1);
          setDoneCards(nextDone);
          setSlideIndex(s + 1);
        }, t);
        t += SLIDE_DURATION;
      }

      schedule(() => {
        const track = trackRef.current;
        if (track) track.style.transition = 'none';
        setSlideIndex(0);
        setDoneCards([0]);
        requestAnimationFrame(() => {
          if (cancelled) return;
          track?.offsetHeight;
          if (track) track.style.transition = '';
          runCycle(200);
        });
      }, t);
    }

    runCycle(1400);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [isVisible]);

  return (
    <Section variant="gray">
      <div
        ref={wrapperRef}
        className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}
      >
        <Container>
          <h2 className={styles.heading}>{t('valueProposition.heading')}</h2>

          <div className={styles.atomContainer}>
            <div className={styles.orbitsLayer}>
              {ELLIPSES.map((e, i) => (
                <div
                  key={i}
                  className={styles.orbitEllipse}
                  style={{
                    width: `calc(var(--orbit-r) * ${e.rxMult * 2})`,
                    height: `calc(var(--orbit-r) * ${e.ryMult * 2})`,
                    transform: `translate(-50%, -50%) rotate(${e.rotation}deg)`,
                  }}
                >
                  <div
                    className={styles.electron}
                    style={{
                      '--e-rx': `calc(var(--orbit-r) * ${e.rxMult})`,
                      '--e-ry': `calc(var(--orbit-r) * ${e.ryMult})`,
                      '--e-dur': `${e.dur}s`,
                      '--e-delay': `${e.delay}s`,
                    } as React.CSSProperties}
                  />
                </div>
              ))}
            </div>

            <div className={styles.orbitTrack} />

            <div className={styles.nucleus}>
              <div className={styles.nucleusGlow} />
              <div className={styles.nucleusCore}>
                <img
                  src="/images/logos/logo-seul-noir.webp"
                  alt="QBP Solutions"
                  className={styles.nucleusLogo}
                />
              </div>
            </div>

            {CARD_SLOTS.map(({ itemIdx, angle }, i) => {
              const item = items[itemIdx];
              const Icon = CARD_ICONS[itemIdx];
              return (
                <div
                  key={itemIdx}
                  className={styles.cardSlot}
                  style={{
                    '--base-angle': `${angle}deg`,
                    '--card-index': i,
                  } as React.CSSProperties}
                >
                  <div className={styles.card}>
                    <div className={styles.cardIcon}>
                      <Icon size={20} color="var(--cyan-technique)" strokeWidth={1.5} />
                    </div>
                    <div className={styles.cardContent}>
                      <div className={styles.cardTitle}>{item.title}</div>
                      <div className={styles.cardSubtitle}>{item.text}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.connector} aria-hidden="true">
            <span className={styles.connectorDot} />
          </div>
        </Container>

        <div className={styles.carousel}>
          <div
            ref={trackRef}
            className={styles.carouselTrack}
            style={{
              transform: `translateX(calc(50% - ${BASE_OFFSET + slideIndex * STEP}px))`,
            }}
          >
            {Array.from({ length: SLIDE_COUNT }, (_, i) => {
              const product = products[SLIDE_PRODUCT_MAP[i]];
              const isCenter = i === slideIndex + 1;
              const isActive = activeCard >= 0 && i === activeCard + 1;
              const isDone = !isActive && doneCards.includes(i);
              return (
                <div
                  key={i}
                  className={`${styles.productDemo} ${isCenter ? styles.productCenter : styles.productSide} ${isActive ? styles.productActive : ''} ${isDone ? styles.productDone : ''}`}
                  aria-hidden={!isCenter}
                >
                  <div className={styles.productLeft}>
                    <div className={styles.productImage}>
                      <img
                        src={product.image}
                        alt={`${product.name} reconditionné`}
                        className={styles.productImg}
                        loading="lazy"
                        onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
                      />
                    </div>
                    <div className={styles.productSpecs}>
                      {product.specs.map((spec, si) => (
                        <span key={si} className={styles.specBadge}>{spec}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.productRight}>
                    <div className={styles.productName}>
                      {product.name}
                    </div>
                    <ul className={styles.checklist}>
                      {product.checks.map((check, ci) => (
                        <li
                          key={ci}
                          className={styles.checkItem}
                          style={{ '--check-index': ci } as React.CSSProperties}
                        >
                          <CircleCheck size={18} className={styles.checkIcon} />
                          <span>{check}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={styles.toast}>
                    <div className={styles.toastIcon}>
                      <Check size={16} strokeWidth={2.5} />
                    </div>
                    <div className={styles.toastContent}>
                      <span className={styles.toastTitle}>{t('valueProposition.productDemo.toastTitle')}</span>
                      <span className={styles.toastText}>{t('valueProposition.productDemo.toastText')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.carouselFadeLeft} />
          <div className={styles.carouselFadeRight} />
        </div>
      </div>
    </Section>
  );
}
